import { apiGateway } from '@va/api-client';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { useCurrentPeriodFilter } from '@va/dashboard/util-hooks';
import { get } from '@va/http-client';
import { getConfig } from '@va/standalone/shared/api-client';
import { isDefaultWebsiteId } from '@va/util/helpers';
import { useFetchData, useLazyDataFetch } from '@va/util/hooks';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

type CountDataType = {
  payload: { count: number };
};

const countResponseMapper = (response: CountDataType) => {
  return response?.payload?.count ?? 0;
};

export const usePollsCount = (shouldFetch = true) => {
  const websiteId = useSelector(getInstanceId);
  const mapper = useCallback(countResponseMapper, []);
  return useLazyDataFetch<number, Error>(
    isDefaultWebsiteId(websiteId) || !shouldFetch ? null : `/websites/${websiteId}/polls/count`,
    undefined,
    mapper,
  );
};

export const useFunnelsCount = (shouldFetch = true) => {
  const { websiteId } = useCurrentPeriodFilter();
  const url = useMemo(() => `v1/websites/${websiteId}/funnels/count`, [websiteId]);

  const fetcher = useCallback(async (url: string) => {
    return await apiGateway.get(url, getConfig());
  }, []);

  return useFetchData<number, Error>(
    isDefaultWebsiteId(websiteId) || !shouldFetch ? null : url,
    {
      revalidateOnFocus: false,
    },
    countResponseMapper,
    fetcher,
  );
};

export const useSurveysCount = (shouldFetch = true) => {
  const websiteId = useSelector(getInstanceId);
  const mapper = useCallback(countResponseMapper, []);
  return useLazyDataFetch<number, Error>(
    isDefaultWebsiteId(websiteId) || !shouldFetch ? null : `/websites/${websiteId}/surveys/count`,
    undefined,
    mapper,
  );
};

export const useHeatmapsCount = (shouldFetch = true) => {
  const websiteId = useSelector(getInstanceId);
  const mapper = useCallback(countResponseMapper, []);
  return useLazyDataFetch<number, Error>(
    () => (isDefaultWebsiteId(websiteId) || !shouldFetch ? null : `/websites/${websiteId}/heatmaps/count`),
    undefined,
    mapper,
  );
};

export const useEventsCount = (shouldFetch = true) => {
  const websiteId = useSelector(getInstanceId);
  const mapper = useCallback((response: { error: any; payload: { used: number; limit: number }; success: boolean }) => {
    return response?.payload?.used ?? 0;
  }, []);
  return useLazyDataFetch<number, Error>(
    isDefaultWebsiteId(websiteId) || !shouldFetch
      ? null
      : `/websites/${websiteId}/tp-counter-api/subscriptions/${websiteId}/touchpoints?feature=event-tracking`,
    undefined,
    mapper,
  );
};

export const useRecordingsCount = (shouldFetch = true) => {
  const websiteId = useSelector(getInstanceId);
  const mapper = useCallback(countResponseMapper, []);
  const url = useMemo(() => `/websites/${websiteId}/recordings/count`, [websiteId]);
  return useLazyDataFetch<number, Error>(isDefaultWebsiteId(websiteId) || !shouldFetch ? null : url, undefined, mapper);
};

export const useApiKeysCount = (shouldFetch = true) => {
  const { websiteId } = useCurrentPeriodFilter();
  const url = useMemo(() => `api/v1/websites/${websiteId}/api-keys/list`, [websiteId]);

  const fetcher = useCallback(async (url: string) => {
    return await apiGateway.post(url, {}, getConfig());
  }, []);
  const mapper = useCallback((response: { meta: { total?: number } }) => {
    return response.meta.total ?? 0;
  }, []);

  return useFetchData<number, Error>(
    isDefaultWebsiteId(websiteId) || !shouldFetch ? null : url,
    {
      revalidateOnFocus: false,
    },
    mapper,
    fetcher,
  );
};

export const useFunnelsHighestConversionRate = () => {
  const websiteId = useSelector(getInstanceId);
  const mapper = useCallback((data: { payload: { highestConversionRate: number } }) => {
    return data?.payload?.highestConversionRate;
  }, []);

  const url = useMemo(() => `/v2/websites/${websiteId}/funnels/highest-conversion-rate`, [websiteId]);

  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const useFunnelsAverageConversionRate = () => {
  const websiteId = useSelector(getInstanceId);
  const mapper = useCallback((data: { payload: { 'average-conversion-rate': number } }) => {
    return data?.payload['average-conversion-rate'];
  }, []);
  const url = useMemo(() => `/v2/websites/${websiteId}/funnels/average-conversion-rate`, [websiteId]);

  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const usePollsAverageCompletionRate = () => {
  const websiteId = useSelector(getInstanceId);
  const url = useMemo(() => `/v2/websites/${websiteId}/polls/average-completion-rate`, [websiteId]);
  const mapper = useCallback((data: { payload: { 'average-completion-rate': number } }) => {
    return data?.payload['average-completion-rate'];
  }, []);
  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const usePollsTotalInitiates = () => {
  const websiteId = useSelector(getInstanceId);
  const url = useMemo(() => `/v2/websites/${websiteId}/polls/initiates`, [websiteId]);
  const mapper = useCallback((data: CountDataType) => {
    return data?.payload?.count;
  }, []);
  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const usePollsTotalResponses = () => {
  const websiteId = useSelector(getInstanceId);
  const url = useMemo(() => `/v2/websites/${websiteId}/polls/responses`, [websiteId]);
  const mapper = useCallback((data: CountDataType) => {
    return data?.payload?.count;
  }, []);
  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const useSurveysAverageCompletionRate = () => {
  const websiteId = useSelector(getInstanceId);
  const url = useMemo(() => `/v2/websites/${websiteId}/surveys/average-completion-rate`, [websiteId]);
  const mapper = useCallback((data: { payload: { 'average-completion-rate': number } }) => {
    return data?.payload['average-completion-rate'];
  }, []);
  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const useSurveysTotalInitiates = () => {
  const websiteId = useSelector(getInstanceId);
  const url = useMemo(() => `/v2/websites/${websiteId}/surveys/initiates`, [websiteId]);
  const mapper = useCallback((data: CountDataType) => {
    return data?.payload?.count;
  }, []);
  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const useSurveysTotalResponses = () => {
  const websiteId = useSelector(getInstanceId);
  const url = useMemo(() => `/v2/websites/${websiteId}/surveys/responses`, [websiteId]);
  const mapper = useCallback((data: CountDataType) => {
    return data?.payload?.count;
  }, []);
  return useLazyDataFetch<number, Error>(url, undefined, mapper);
};

export const useSurveysStats = () => {
  const { data: averageCompletionRate = 0, isLoading: isLoadingAvgCompletionRate } = useSurveysAverageCompletionRate();
  const { data: totalInitiates = 0, isLoading: isLoadingTotalInitiates } = useSurveysTotalInitiates();
  const { data: totalResponses = 0, isLoading: isLoadingTotalResponses } = useSurveysTotalResponses();

  const isLoading = isLoadingAvgCompletionRate || isLoadingTotalInitiates || isLoadingTotalResponses;

  return {
    averageCompletionRate,
    totalInitiates,
    totalResponses,
    isLoading,
  };
};

type RecordingsStatsResponse = {
  meta: {};
  payload: {
    averageSessionRecordingDuration: 0;
    sessionRecordingsCount: 0;
    sessionRecordingsSeenCount: 0;
    sessionRecordingsStarredCount: 0;
  };
};

export const useRecordingsStats = () => {
  const websiteId = useSelector(getInstanceId);
  const { from, until } = useCurrentPeriodFilter();

  const fetcherFn = useCallback(async (url: string, queryData: Record<string, unknown>) => {
    const res = await get<RecordingsStatsResponse>(url, queryData);
    return res.payload;
  }, []);

  return useLazyDataFetch<RecordingsStatsResponse['payload']>(
    [`/v2/websites/${websiteId}/sessions/recordings/metrics`, { from, until }],
    undefined,
    undefined,
    fetcherFn,
  );
};

export const useFunnelsStats = () => {
  const { data: highestConversionRate = 0, isLoading: isLoadingHighestRate } = useFunnelsHighestConversionRate();
  const { data: averageConversionRate = 0, isLoading: isLoadingAverageRate } = useFunnelsAverageConversionRate();

  const isLoading = isLoadingAverageRate || isLoadingHighestRate;
  return {
    averageConversionRate,
    highestConversionRate,
    isLoading,
  };
};

export const usePollsStats = () => {
  const { data: averageCompletionRate = 0, isLoading: isLoadingAvgCompletionRate } = usePollsAverageCompletionRate();
  const { data: totalInitiates = 0, isLoading: isLoadingTotalInitiates } = usePollsTotalInitiates();
  const { data: totalResponses = 0, isLoading: isLoadingTotalResponses } = usePollsTotalResponses();

  const isLoading = isLoadingAvgCompletionRate || isLoadingTotalInitiates || isLoadingTotalResponses;

  return {
    averageCompletionRate,
    totalInitiates,
    totalResponses,
    isLoading,
  };
};
